import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { ConfirmationModal } from '@components/ConfirmationModal';
import { useEntityStore, useSchemaStore } from '@ContractBuilder/store';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { getBlockValidationErrors, getContractValidationErrors, isDocumentValid } from '@helpers/status';
import type { RootResourceStatus } from '@root/@types/types';
import { DocumentMode } from '@root/@types/types';
import { useEffectOnce } from '@src/hooks';

import { getResolvedConfig, getTransitionConfig } from './helpers';
import { InvalidInfo } from './InvalidInfo';

interface ConfirmStatusModalProps {
  handleClose: () => void;
  onConfirm: () => Promise<void>;
  status: RootResourceStatus;
}

const ConfirmStatusModal: FC<ConfirmStatusModalProps> = ({ handleClose, onConfirm, status }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
    handleClose();
  }, [onConfirm, handleClose]);

  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { mode } = getDocumentMode();
  const { isValid, validate } = useSchemaStore(({ isValid, validate }) => ({ isValid, validate }));

  const isSubmission = mode === DocumentMode.SUBMISSIONS;
  const isEndorsement = mode === DocumentMode.ENDORSEMENTS;
  const currentStatus = submission?.status;

  const blockErrors = useMemo(() => {
    return getBlockValidationErrors(isSubmission || isEndorsement, submission);
  }, [isSubmission, isEndorsement, submission]);

  const contractErrors = useMemo(() => {
    return getContractValidationErrors(isSubmission || isEndorsement, isValid, submission);
  }, [isSubmission, isEndorsement, isValid, submission]);

  useEffectOnce(() => {
    if (isSubmission || isEndorsement) {
      validate();
    }
  });

  const isDocValid = isDocumentValid(blockErrors, contractErrors);

  const hasEndorsements = (submission?.endorsementsCount ?? 0) > 0;

  const config = useMemo(() => {
    return getTransitionConfig(currentStatus, status, mode, isDocValid, { hasEndorsements });
  }, [currentStatus, status, mode, isDocValid, hasEndorsements]);

  if (!config) {
    return null;
  }

  const validationRequired = config.validation?.required === true;
  const shouldShowValidationErrors = validationRequired && !isDocValid;

  const context = { fromStatus: currentStatus, toStatus: status, documentMode: mode, hasEndorsements };
  const { title, content, confirmText } = getResolvedConfig(config, context);

  return (
    <ConfirmationModal
      confirmKind={config.buttons.confirm.variant || 'primary'}
      confirmLabel={confirmText}
      description={shouldShowValidationErrors ? undefined : <span className="whitespace-pre-line">{content}</span>}
      hideCancel={!config.buttons.cancel}
      icon={shouldShowValidationErrors ? 'danger' : (config.icon?.type ?? 'valid')}
      isLoading={isLoading}
      onClose={handleClose}
      onConfirm={shouldShowValidationErrors ? handleClose : handleConfirm}
      title={title}
    >
      {shouldShowValidationErrors && (
        <div className="mt-3">
          <InvalidInfo blockErrors={blockErrors} contractErrors={contractErrors} />
        </div>
      )}
    </ConfirmationModal>
  );
};

export default ConfirmStatusModal;
